import { Integer } from '@type-r/ext-types';
import { AttributesMixin, define, mixins, Model, type } from '@type-r/models';
import { asCurrency, asTimelessDate, MultiSelectFilter } from 'lqx-react/lib';
import { CompanyModel, globalStore, PlatformCompany } from 'api';
import { createdUpdatedAttrs, matchString } from 'api/common';
import { fetchJson } from 'api/endpoint';
import { CalculatedDisputeStatus, GetInvoiceStatusAsText, InvoiceStatus } from 'api/invoice';
import { root } from './common';
import { UnresolvedRelationship } from './unresolvedRelationship';
import { OpenInvoicesFilter } from '../matching/unreconciled/open-invoices';

const digitizedSources = ['OM', 'DIGI', 'PTS'];

@define
export class RecsInvoice extends Model {
    static attributes = {
        ...createdUpdatedAttrs,
        conversationId : Integer,
        disputeStatus:String,
        invoiceNumber : String,
        invoiceDate : Date,
        invoiceDueDate : Date,
        // customerId : Integer,
        // supplierId : Integer,
        remittanceAdviceIds : Array,
        promiseToPayDate : Date,
        invoiceStatus : String,
        currency : String,
        invoicedAmount : Number,
        paymentPeriod : Integer,
        discountPeriod : Integer,
        paymentTerms : Integer,
        unreconciledAmount : Number,
        paymentInfoIds : Array,
        matchedPayments : [{
            id : String,
            originType : String,
            isReconciled : Boolean,
        }],
        additionalReferenceIdentifier: String,
        emailSentOn : Date,
        source : String,
        hasSocComments: Boolean,
        externalStatus: String,
        internalStatusId: Number,
        platformCode: type(String).dontSave,
        metadata: {},
        paymentMatchedId : type(Number).dontSave,
        customerPlatformCompanyId : Integer,
        supplierPlatformCompanyId : Integer,
    }

    get age() {
        const date = this.promiseToPayDate || this.invoiceDueDate;
        return Math.trunc((new Date().setHours(0, 0, 0, 0) - date.getTime()) / 86400000);
    }

    filter(filter: OpenInvoicesFilter) {
        const text = filter.filterAll;

        return (!text
            || matchString(this.invoiceNumber, text)
            || matchString(String(this.unreconciledAmount), text)
            || matchString(String(this.additionalReferenceIdentifier), text)
            || matchString(asCurrency(this.unreconciledAmount), text)
            || matchString(String(this.invoicedAmount), text)
            || matchString(asCurrency(this.invoicedAmount), text)
            || matchString(asTimelessDate(this.invoiceDueDate), text)
            || matchString(asTimelessDate(this.promiseToPayDate), text))
            && (!filter.supplierName || filter.supplierName.isSuccessful(this.supplierName))
            && (!filter.customerName || filter.customerName.isSuccessful(this.customerName))
            && (!filter.invoiceDueDate.from || !filter.invoiceDueDate.to || (this.invoiceDueDate >= filter.invoiceDueDate.from && this.invoiceDueDate <= filter.invoiceDueDate.to));
    }

    get displayAmount(){
        return this.unreconciledAmount;
    }

    get estimatedPayDate(){
        return this.promiseToPayDate || this.invoiceDueDate;
    }

    get customer() : PlatformCompany {
        return globalStore.platformCompanies.get(this.customerPlatformCompanyId as any);
    }

    get supplier() : PlatformCompany {
        return globalStore.platformCompanies.get(this.supplierPlatformCompanyId as any);
    }

    get customerName() : string {
        return this.customer ? this.customer.name : `Customer #${this.customerPlatformCompanyId}`;
    }

    get supplierName() : string {
        return this.supplier ? this.supplier.name : `Supplier #${this.supplierPlatformCompanyId}`;
    }

    get displayStatus(): string{
        return GetInvoiceStatusAsText(this.disputeStatus as any || this.invoiceStatus);
    }

    get calculatedStatus(): CalculatedDisputeStatus | InvoiceStatus {
        return this.disputeStatus as any || this.invoiceStatus;
    }

    get isDigitized(): boolean {
        return digitizedSources.indexOf(this.source) !== -1;
    }

    static async searchOpenInvoices(paymentId, params) : Promise<[RecsInvoice]> {
        const url = `/api/v2/recs/invoice/open/${paymentId}?${params.toString()}`;

        return await fetchJson('GET', url);
    }

    static async getOpenInvoicesSummary(paymentId, params) {
        const url = `/api/v2/recs/invoice/open-summary/${paymentId}?${params.toString()}`;

        return await fetchJson('GET', url);
    }
}

export interface RecsInvoice extends AttributesMixin<typeof RecsInvoice> {
    invoiceStatus : InvoiceStatus
}

export async function execSetEmailSentFlag(ids, flag: boolean) {
    const url = `${root}invoice/set-email-sent-flag/${flag}`;
    return await fetchJson('POST', url, ids);
  }

export async function execInvoiceLookup(invoiceNumber) : Promise<UnresolvedRelationship[]> {
    const url = `${root}invoice/invoice-lookup?invoiceNumber=${invoiceNumber}`;
    return await fetchJson('GET', url);
}
